import { removeUrlParams } from '@doda/common/src/utils/urlUtil';
import { useEffect, useState, ComponentProps } from 'react';
import { queryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useAppSelector } from '@doda/common/src/redux/hooks';
import { Select } from '@doda/io-react/pc';
import { useRouter } from 'next/router';
import { REMOVE_PARAM_LIST, DEFAULT_OPTION } from '../../constants';
import { createSortUrl } from '../../logics/createSortUrl';

/**
 * カノニカル形式もしくは動的URLのベースとなるURLを生成する
 * @param {Object} params
 * @param {string} params.baseUrl ベースとなるURL
 * @param {ComponentProps<typeof Select>['options']} params.options 並び替えの選択肢
 * @param {Record<string, string>} params.orderAdditionalParam 並び替え時の解析系等の追加クエリパラメータ
 * @returns {Object} 並び替えのための関数と状態 (onChange関数、orderOptions、hasSuffix、defaultValue)
 */
export const useCreateSort = ({
  options,
  baseUrl,
  orderAdditionalParam = {},
  isStaticUrl = false,
}: {
  options: ComponentProps<typeof Select>['options'];
  baseUrl: string;
  orderAdditionalParam?: Record<string, string>;
  isStaticUrl?: boolean;
}) => {
  const REMOVE_PARAMS_FOR_SORT = [
    ...REMOVE_PARAM_LIST,
    'rlist',
    'prsrt',
    'page',
  ];
  const queryParams = useAppSelector(queryParamsSelector);
  const { ds = '0' } = queryParams;
  const [orderOptions, setOrderOptions] = useState(options);
  const optionValues = orderOptions.map(({ value }) => value);
  const defaultValue = optionValues.includes(ds) ? ds : '0';
  const hasSuffix = defaultValue !== '0';
  const removedUrlForSort = removeUrlParams(baseUrl, REMOVE_PARAMS_FOR_SORT);
  const { pathname } = useRouter();

  const onChange = (value: string) => {
    // 並べ替えによる遷移
    const url = createSortUrl(
      value,
      isStaticUrl,
      queryParams,
      removedUrlForSort,
      orderAdditionalParam,
      pathname
    );
    if (defaultValue !== value) window.location.href = url;
  };

  useEffect(() => {
    // 並び替えの値が設定されている場合は「条件で並べ替え」を選択肢から外す
    // 「条件で並べ替え」をデフォルトとして表示させるため、useEffect内で行う
    setOrderOptions((prevOptions) =>
      prevOptions.filter(({ value }) => value !== DEFAULT_OPTION.value)
    );
  }, []);

  return { onChange, orderOptions, hasSuffix, defaultValue };
};
